import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("verifying"); // verifying | success | already | error
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  // Guard against React 18 StrictMode double-invocation in dev (would otherwise
  // cause the first-visit copy to flip from "Email confirmed." to "Already confirmed.")
  const verifyRan = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Missing or invalid verification link.");
      return;
    }
    if (verifyRan.current) return;
    verifyRan.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        setEmail(data.email || "");
        setStatus(data.already ? "already" : "success");
      } catch (err) {
        setStatus("error");
        setMessage(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "We couldn't verify this link. It may have expired."
        );
      }
    })();
  }, [token]);

  return (
    <div
      data-testid="verify-email-page"
      className="px-6 md:px-12 lg:px-24 py-32 max-w-2xl mx-auto text-center"
    >
      <p className="label-eyebrow text-[#1E3A8A]">— Email Confirmation</p>

      {status === "verifying" && (
        <>
          <h1 className="font-serif-display text-5xl tracking-tighter font-light mt-4">
            Confirming your email…
          </h1>
          <p className="text-[#555] mt-6 font-mono-aam text-xs">Validating link</p>
        </>
      )}

      {(status === "success" || status === "already") && (
        <>
          <h1 className="font-serif-display text-6xl tracking-tighter font-light mt-4">
            <span className="italic text-[#1E3A8A]">
              {status === "already" ? "Already confirmed." : "Email confirmed."}
            </span>
          </h1>
          <p className="text-[#333] mt-6 text-lg font-light" data-testid="verify-email-success">
            {status === "already"
              ? "This account is already verified — you can sign in normally."
              : "Your account is now active. Welcome to AfricaArtMasters."}
          </p>
          {email && (
            <p className="font-mono-aam text-sm text-[#555] mt-3">{email}</p>
          )}
          <div className="mt-12 flex justify-center gap-4">
            <Link
              to="/login"
              data-testid="verify-email-login-link"
              className="label-eyebrow border border-[#111] px-8 py-4 hover:bg-[#111] hover:text-white transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/gallery"
              className="label-eyebrow border border-[#111] px-8 py-4 hover:bg-[#111] hover:text-white transition-colors"
            >
              Browse the gallery
            </Link>
          </div>
        </>
      )}

      {status === "error" && (
        <>
          <h1 className="font-serif-display text-5xl tracking-tighter font-light mt-4">
            Link expired or invalid
          </h1>
          <p className="text-[#555] mt-6" data-testid="verify-email-error">{message}</p>
          <div className="mt-10 flex justify-center gap-4">
            <Link
              to="/login"
              className="label-eyebrow border border-[#111] px-8 py-4 hover:bg-[#111] hover:text-white transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              className="label-eyebrow border border-[#111] px-8 py-4 hover:bg-[#111] hover:text-white transition-colors"
            >
              Register again
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
